/**
 * Created by sdesai on 8/12/2022
 * File: DeleteInstanceModal
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Banner,
  Body,
  Button,
  Bold,
  Checkbox, LoadingIcon
} from '@panwds/react-ui'
import { ACTIVATION_UI_ERROR_CODES } from '../../../../activation/ErrorCodes'
import SubscriptionService from '../../../../activation/services/SubscriptionService'
import {
  Modal,
  ModalFooter,
  ModalHeader,
} from '../../../../common/components/modal/Modal'
import { APP_ID_DSS_ID } from '../../../../common/constants/GlobalConstants'
import {
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT
} from '../../../../common/constants/TestingDataIds'
import { loadTsgInstances } from '../../../../common/utils/Sparky'
import { isNonemptyArray } from '../../../../hub/utils/common'
import ZIndex from '../../../../common/components/zIndex/ZIndex'
import '../../../../iam/styles/Modals.scss'
import {
  ConnectedTable,
  TableProvider
} from '@panwds/react-table'
import styled from 'styled-components'
import CIESharingTsgRenderer from '../cellRenderer/CIESharingTsgRenderer'

const TableWrapper = styled.div`
  div[data-testid="checkBox"] {
    border-right: 1px solid #DADBDB !important;
  }
  div[data-table-column-id="checkBox"]{
    border-right: 1px solid #DADBDB !important;
  }
  `
const Footer = ({ isLoading, closeModal, error, onClickSave }) => {
  return (
    <ModalFooter>
      <Button
        data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
        onClick={closeModal}
        appearance="secondary"
        disabled={isLoading}
      >Cancel</Button>
      <Button
        onClick={onClickSave}
        appearance="primary"
        data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
        disabled={isLoading || error }
      >Save</Button>
    </ModalFooter>
  )
}


const CIESharingModal = ({ closeModal, isOpen, instance, app, selectedTreeNode, flattenTree }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [indeterminate, setIndeterminate] = useState(false)
  const [tsgSelection, setTsgSelection] = useState({})
  const [allState, setAllState] = useState(false)
  const [instanceMap, setTsgInstanceMap] = useState()

  /**
   *  Recursive method to pull all the child tsgs
   * @type {(function(*, *=): void)|*}
   */
  const getAllTsg = useCallback((object, allTsg) => {
    if (isNonemptyArray(object?.children)) {
      for (const child of object?.children) {
        allTsg.push(child.id)
        if (isNonemptyArray(child?.children)) {
          getAllTsg(child, allTsg)
        }
      }
    }
  }, [])

  const onClickSave = useCallback(() => {
    const keys = Object.keys(tsgSelection)
    setIsLoading(true)
    const tsgList = []
    keys.forEach(_each => {
      if (tsgSelection[_each] && !instanceMap[_each].dssInstance) {
        tsgList.push(_each)
      }
    })
    if (isNonemptyArray(tsgList)) {
      const request = {
        app_id: APP_ID_DSS_ID,
        attached_tsg_ids: tsgList,
        attached_tsg_action: 'ADD',
        support_account_id: instance?.support_account_id,
        region: instance?.platform_region ?? instance?.region,
        is_eula_accepted: true
      }
      SubscriptionService.activateInstance.post(request, selectedTreeNode.id).then(async () => {
        closeModal()
      }).catch((err) => {
        setIsLoading(false)
        setError(err?.message || ACTIVATION_UI_ERROR_CODES.ACTIVATION_FAILED)
      })
    }
    else {
      setIsLoading(false)
      setError(ACTIVATION_UI_ERROR_CODES.NO_TSG_SELECTED)
    }
  }, [tsgSelection, instance?.support_account_id, instance?.platform_region, instance?.region, selectedTreeNode.id, instanceMap, closeModal])

  const getAllTsgInstances = useCallback(async (allTsg) => {
    const tsgInstanceMap = {}
    for (const tsg_id of allTsg) {
      const tsgInstance = await loadTsgInstances(tsg_id)
      let isCieExisting = 'No'
      let dssInstance
      if (tsgInstance && isNonemptyArray(tsgInstance)) {
        dssInstance = tsgInstance.find(_each => _each.app_id === APP_ID_DSS_ID)
        if (dssInstance && dssInstance?.original_tsg_id) {
          if (dssInstance?.original_tsg_id === selectedTreeNode.id) {
            isCieExisting = 'same'
          }
          else {
            isCieExisting = 'other'
          }
        }
        else if (dssInstance && !dssInstance?.original_tsg_id) {
          isCieExisting = 'new'
        }
        else if (!dssInstance) {
          isCieExisting = 'No'
        }
      }
      tsgInstanceMap[tsg_id] = { isCieExisting, dssInstance }
    }
    return tsgInstanceMap
  }, [selectedTreeNode.id])

  /**
   *  Get all the instances for all the child tsgs
   * @type {{}}
   */
  useMemo(async () => {
    const allTsg = []
    setIsLoading(true)
    getAllTsg(selectedTreeNode, allTsg)
    getAllTsgInstances(allTsg).then(data => {
      setIsLoading(false)
      setTsgInstanceMap(data)
    })
  }, [getAllTsg, getAllTsgInstances, selectedTreeNode])

  /**
   *  To handle the onclick of the checkbox
   * @type {(function(*): void)|*}
   */
  const updateTsgSharingList = useCallback((tsg_id) => {
    const currentSelection = tsgSelection[tsg_id]
    const response = { ...tsgSelection, [tsg_id]: !currentSelection }
    setTsgSelection({
      ...response
    })
    const keys = Object.keys(response)
    const isAllTrue = isNonemptyArray(keys) ? keys?.every(_each => response[_each]) : false
    const isAllFalse = isNonemptyArray(keys) ? keys?.every(_each => !response[_each]) : false
    if (!isAllTrue && !isAllFalse) {
      setIndeterminate(true)
    }
    else if (isAllFalse) {
      setIndeterminate(false)
      setAllState(false)
    }
    else if (isAllTrue) {
      setIndeterminate(false)
      setAllState(true)
    }
  }, [tsgSelection])

  const showAssociatedCieName = useCallback((tsg_d) => {
    const dssInstance = instanceMap[tsg_d]?.dssInstance
    const isCieExisting = instanceMap ? (instanceMap[tsg_d]?.isCieExisting ?? 'No') : 'No'
    if (dssInstance && dssInstance?.original_tsg_id) {
      const parentTsgName = flattenTree?.find(_each => _each.id === dssInstance?.original_tsg_id)
      return <Body size='sm'>
        <Bold>Shared From - </Bold>
        ( {parentTsgName?.display_name ?? ''} )
      </Body>
    }
    else if (isCieExisting === 'new') {
      return <Body size='sm'>
        <Bold>Standalone CIE Instance</Bold>
      </Body>
    }

  }, [flattenTree, instanceMap])

  /**
   *   Recursive method to display the checkBoxes
   *   if isCieExisting has 'No' allow  the user to select or deselect the checkbox
   *   if isCieExisting is other or new ,show indeterminate state in the checkBox
   *   if isCieExisting is same, make the check box selected and disabled
   * @type {(function(*, *=, *): void)|*}
   */
  const recursiveTreeBuild = useCallback((object, response, level) => {
    if (isNonemptyArray(object?.children)) {
      for (const child of object?.children) {
        const isCieExisting = instanceMap ? (instanceMap[child.id]?.isCieExisting ?? 'No') : 'No'
        const checkBoxState = (isCieExisting === 'other' || isCieExisting === 'new')
        const isDisabled = (isCieExisting === 'other' || isCieExisting === 'new' || isCieExisting === 'same')
        response.push(
          {
            checkBox: <div><Checkbox
              style={isDisabled ? { backgroundColor: '#929191' } : null}
              dataTestId={'cie-sharing-checkbox'}
              label={child.display_name}
              name={child.id}
              checked={isCieExisting === 'same' || tsgSelection[child.id]}
              disabled={isDisabled}
              indeterminate={checkBoxState}
              onClick={() => updateTsgSharingList(child.id)}
            /></div>,
            displayName: child.display_name,
            isDisabled,
            level,
            cieName: isDisabled ? showAssociatedCieName(child.id) : ''
          }
        )
        if (isNonemptyArray(child?.children)) {
          recursiveTreeBuild(child, response, level + 1)
        }
      }
    }
  }, [instanceMap, showAssociatedCieName, tsgSelection, updateTsgSharingList])

  useEffect(() => {
    const data = {}
    const updateChild = (object) => {
      if (isNonemptyArray(object?.children)) {
        for (const child of object?.children) {
          data[child.id] = false
          if (isNonemptyArray(child?.children)) {
            updateChild(child)
          }
        }
      }
    }
    updateChild(selectedTreeNode)
    setTsgSelection(data)
  }, [selectedTreeNode])

  const displayTsgTree = useMemo(() => {
    const response = []
    if (!isLoading) {
      recursiveTreeBuild(selectedTreeNode, response, 0)
    }
    return response
  }, [isLoading, recursiveTreeBuild, selectedTreeNode])

  /**
   * Handle All onclick function, it updates the checkboxes
   * @type {(function(*): void)|*}
   */
  const updateOnClickOfAll = useCallback((value) => {
    const keys = Object.keys(tsgSelection)
    setAllState(!allState)
    setIndeterminate(false)
    const response = {}
    keys?.length && keys.forEach(_each => {
      response[_each] = value.currentTarget.checked
    })
    setTsgSelection(response)
  }, [allState, tsgSelection])

  const getAllButton = useMemo(() => {
    return <div className="cm-flex cm-flex-.2 cm-flex-col">
      <div className="tw-flex tw-flex-row tw-ml-4">
        <Checkbox
          label='All'
          name={'All'}
          indeterminate={indeterminate}
          checked={allState}
          onClick={updateOnClickOfAll}
        />
        <Body size='sm' addClassName={'tw-pl-1'}>All</Body>
      </div>
    </div>
  }, [allState, indeterminate, updateOnClickOfAll])

  const onCloseError = useCallback(() => {
    setError('')
  }, [])

  const CheckBoxDisplay = ({ row }) => {
    const { checkBox } = row?.original
    return <div>{checkBox}</div>
  }
  const title = `Manage Sharing of ${app?.display_name} (${instance?.tenant_id})`
  const columns = useMemo(() => {
    return [{
      accessor: 'checkBox',
      Header: 'Share',
      Cell: (props) => <CheckBoxDisplay {... props } />,
      width: 100
    },
    {
      accessor: 'displayName',
      Header: 'Tenant',
      width: 390,
      Cell: (props) => <CIESharingTsgRenderer {... props } />
    },
    {
      accessor: 'cieName',
      Header: 'Cloud Identity Engine Instance',
      width: 290
    }
    ]
  }, [])
  return (
    <ZIndex>
      <Modal style={{ top: '12%', height: '560px', width: '800px' }} size='md' onClose={closeModal} isOpen={isOpen}>
        <div className='cm-flex-1 tw-w-full tw-fixed tw-top-1'>
          <ModalHeader title={title }/>
        </div>
        {isLoading ? <LoadingIcon style={{ top: '50%', left: '50%' }} className="tw-relative tw-left-52" size="4xl"/> : <div className='cm-flex-1 cm-m-2 tw-fixed' style={{ top: '50px' }}><div className='cm-mb-1'>
          <Body size='xs' addClassName='cm-pl-2'><Bold>Select which tenants you will share this Cloud Identity Engine instance:</Bold></Body>
          <Body size='xs' addClassName='cm-pl-2'>Note: You can only share this Cloud Identity Engine (CIE) instance to tenants without CIE.</Body>
          <Body size='xs' addClassName='cm-pl-10'>Sharing of Cloud Identity Engine instance is not automatically inherited by child tenants and must be assigned individually.</Body>
        </div>{getAllButton}
        </div>
        }
        {!isLoading && <TableWrapper>
          <div className='cm-flex cm-flex-col tw-relative tw-top-32 tw-overflow-y-auto cm-mx-2 cm-my-2' style={{ maxHeight: '330px', border: '1px solid #DADBDB', borderRadius: '4px' }}>
            <TableProvider columns={columns} data={displayTsgTree}>
              <ConnectedTable />
            </TableProvider>
          </div>
        </TableWrapper>}
        <div className='tw-fixed tw-bottom-1 tw-w-full'>
          {error?.length ? <Banner type="inline" appearance="error" showIcon={true} onClose={onCloseError}>
            {error}
          </Banner> : <></>}
          <Footer isLoading={isLoading} error={error} closeModal={closeModal} onClickSave={onClickSave} />
        </div>
      </Modal>
    </ZIndex>
  )
}
export default CIESharingModal
